/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { addDays, differenceInDays } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Chip, Divider, Stack } from "@mui/material";

import TimeoffLayout from "components/Screens/Timeoff/Layout";
import CustomAlert from "components/common/Alert";
import Daterange from "components/common/Daterange";
import RadioButton from "components/common/RadioButtons";
import Button from "components/common/Button";
import CustomDropdown from "components/common/Dropdown";
import FileUploader from "components/common/FileUploader";
import PageLoader from "components/common/PageLoader";
import { convertDate } from "components/Screens/CommonUtils";
import { AlertType } from "components/common/Snackbar";
import AttachDocuments from "components/Screens/Timeoff/ManageTimeoff/RequestTimeoff/AttachDocuments";
import RegulationChip from "components/Screens/Timeoff/ManageTimeoff/RequestTimeoff/RegulationChip/RegulationChip";
import SwappedLeavesDateSelection from "components/Screens/Timeoff/ManageTimeoff/RequestTimeoff/SwappedLeavesDateSelection";

import { FetchLeaveTypes } from "store/actions/Timeoff/LeaveTypes";
import {
  createLeaveRequest,
  createLeaveRequestV2,
  createLeaveRequestV4,
  getLeaveCount,
  getLeaveCountV2,
  getLeaveCountV4,
  uploadMedicalDocs,
} from "services/useTimeoffService";
import { showSnackbar } from "store/actions/Utility";
import { GetAllHolidays } from "store/actions/Holidays";
import { FetchUpcomingLeaves } from "store/actions/Timeoff/UpcomingLeaves";

import { baseUrl } from "util/APIBaseUrl";
import { getDatesBetweenDateRange } from "util/combineUpcomingLeavesAndHolidays";

import { UNLIMITED } from "components/Screens/Timeoff/ManageTimeoff/constants";
import { leaveTypeIds } from "./Enum";

import LeftArrowSmall from "assets/images/left-arrow.svg";
import DateRangeIcon from "assets/images/date-range-picker.svg";
import CheckMarkIcon from "assets/images/check-marks.svg";
import RejectedIcon from "assets/images/rejected-icon.svg";

import "./RequestTimeoff.scss";

function RequestTimeoff() {
  const location = useLocation();
  const { anniversaryBasedTimeOff, swappedLeavesRevamp, futureCycleLeavesFiling } = useFlags();
  const selectedLeaveTypeIdFromCard = location.state?.selectedLeaveTypeId;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const leaveTypes = useSelector((state) => state?.TimeoffLeaveTypes?.leaveTypes || []);
  const upcomingLeaves = useSelector((state) => state?.TimeoffUpcomingLeaves?.upcomingLeaves);
  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);
  const allHolidays = useSelector((state) => state?.Holidays?.allHolidays);

  const [validDates, setValidDates] = useState();
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [selectedLeaveTypeRules, setSelectedLeaveTypeRules] = useState(null);
  const [showUploadDocumentSection, setShowUploadDocumentSection] = useState(false);
  const [submittingTimeoffRequest, setSubmittingTimeoffRequest] = useState({ state: false, showSnackbar: false });
  const [timeoffRequestLoader, setTimeoffRequestLoader] = useState(true);
  const [selectedTimeOff, setSelectedTimeOff] = useState({});
  const [leavesCalendar, setLeavesCalendar] = useState([]);
  const [dayDuration, setDayDuration] = useState("Full Day");
  const [leaveRequest, setLeaveRequest] = useState({
    selectedDateRange: { startDate: new Date(), endDate: new Date() },
    selectedLeaveType: null,
    description: null,
    swapDays: null,
    timeOffDays: null,
    swapDaysInfo: null,
  });

  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [swappedLeavesData, setSwappedLeavesData] = useState(null);

  const [canUserUploadDocuments, setCanUserUploadDocuments] = useState(
    !anniversaryBasedTimeOff &&
      (anniversaryBasedTimeOff ? leaveRequest?.additionalDocumentsRequired : leaveRequest?.attachDocument)
  );

  const timeoffTypeLabel = timeOffTypes.find((leave) => leave?.key === leaveRequest?.selectedLeaveType)?.label;
  const selectedLeaveType = timeOffTypes.find((leave) => leave?.key === leaveRequest?.selectedLeaveType);

  const isTimeOffAllowedCountry = userAdditionalInfo?.timeoffCountry === "Pakistan";
  const isSwappedLeaveType = leaveRequest?.selectedLeaveType === leaveTypeIds.Swapped;
  const selectedDays =
    differenceInDays(
      new Date(leaveRequest?.selectedDateRange?.endDate),
      new Date(leaveRequest?.selectedDateRange?.startDate)
    ) + 1;

  const showDayDurationOptions =
    selectedDays === 1 && selectedLeaveType?.halfLeaveAllowed && isTimeOffAllowedCountry && anniversaryBasedTimeOff;

  // eslint-disable-next-line consistent-return
  const handleCreateLeaveRequest = async () => {
    if (canUserUploadDocuments && filesToUpload.length === 0) {
      return setSubmittingTimeoffRequest({
        state: false,
        showSnackbar: true,
        type: "failure",
        snackbarMessage: "Upload doctor's note to request sick leave",
      });
    }
    const payload = { ...leaveRequest };

    if (isSwappedLeaveType && swappedLeavesData && swappedLeavesRevamp) {
      const { swapDays, timeOffDays, swapDaysInfo } = swappedLeavesData;
      payload.swapDays = swapDays;
      payload.selectedDateRange = timeOffDays;
      payload.swapDaysInfo = swapDaysInfo;
    }

    if (anniversaryBasedTimeOff && dayDuration && !isSwappedLeaveType) {
      payload.duration = dayDuration;
    }

    if (anniversaryBasedTimeOff) {
      payload.leaveDocuments = filesToUpload.map((file) => ({
        ...file,
        extension: `.${file?.fileName?.split(".")?.pop()}`,
      }));
    }

    setSubmittingTimeoffRequest({
      state: true,
      showSnackbar: false,
    });

    const res = futureCycleLeavesFiling
      ? await createLeaveRequestV4(payload)
      : anniversaryBasedTimeOff
        ? await createLeaveRequestV2(payload)
        : await createLeaveRequest(payload);

    const { payload: resp } = res;

    if (!res?.ok || !resp?.success) {
      const errorMessage = !res?.ok ? `${resp?.error[0]?.description}` : resp?.message;
      return setSubmittingTimeoffRequest({
        state: false,
        showSnackbar: true,
        type: "failure",
        snackbarMessage: errorMessage || "An error occurred while requesting for leave.",
      });
    }

    if (!anniversaryBasedTimeOff) {
      await uploadMedicalDocs(resp?.requestId, filesToUpload);
    }

    setSubmittingTimeoffRequest({
      state: false,
      showSnackbar: true,
      type: "success",
      snackbarMessage:
        "Your request has been sent to your employer. You will be notified once he/she responds to your request",
    });

    setTimeout(() => {
      navigate("/main/timeoff");
    }, 0);
  };

  const calculateTotalDays = () => {
    const pending = selectedLeaveTypeRules?.pending || 0;
    const scheduled = selectedLeaveTypeRules?.scheduled || 0;

    return pending + scheduled;
  };

  const calculateTotalTakenDays = () => {
    const pending = selectedLeaveTypeRules?.pending || 0;
    const scheduled = selectedLeaveTypeRules?.scheduled || 0;
    const taken = selectedLeaveTypeRules?.taken || 0;

    return pending + scheduled + taken;
  };

  const getAllowedTimeOff = () => {
    if (!selectedLeaveTypeRules) return " ";
    if (isSwappedLeaveType && swappedLeavesRevamp) return "N/A";

    const { noOfLeaves, leaveQuota } = selectedLeaveTypeRules;

    if (!anniversaryBasedTimeOff || leaveQuota === -1) return UNLIMITED;
    if (leaveRequest?.selectedLeaveType === leaveTypeIds.UNPAID) return UNLIMITED;

    return `${noOfLeaves - calculateTotalTakenDays()}`;
  };

  const calculateNoOfLeaves = async () => {
    if (isSwappedLeaveType && swappedLeavesRevamp) {
      setSelectedTimeOff({ leaves: 0, holidays: [], success: true });
      return;
    }

    if (leaveRequest?.selectedLeaveType) {
      let tempSelectedTimeOff;
      if (futureCycleLeavesFiling) {
        tempSelectedTimeOff = await getLeaveCountV4(
          leaveRequest?.selectedDateRange?.startDate,
          leaveRequest?.selectedDateRange?.endDate,
          leaveRequest?.selectedLeaveType,
          dayDuration,
          null,
          null,
          swappedLeavesRevamp
        );
      } else if (anniversaryBasedTimeOff) {
        tempSelectedTimeOff = await getLeaveCountV2(
          leaveRequest?.selectedDateRange?.startDate,
          leaveRequest?.selectedDateRange?.endDate,
          leaveRequest?.selectedLeaveType,
          dayDuration,
          null,
          null,
          swappedLeavesRevamp
        );
      } else {
        tempSelectedTimeOff = await getLeaveCount(
          leaveRequest?.selectedDateRange?.startDate,
          leaveRequest?.selectedDateRange?.endDate,
          leaveRequest?.selectedLeaveType
        );
      }
      if (tempSelectedTimeOff.status === 200) {
        setSelectedTimeOff(tempSelectedTimeOff?.payload);
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: tempSelectedTimeOff?.payload?.error[0]?.description,
            dispatch,
          })
        );
      }
    }
  };

  // eslint-disable-next-line consistent-return
  const getChipValuesForRegulations = (timeoffType, noOfDays) => {
    if (timeoffType === "Notice Period") {
      const daysDifference = differenceInDays(new Date(leaveRequest?.selectedDateRange?.startDate), new Date()) + 1;

      if (daysDifference > noOfDays) {
        return {
          noOfDays: daysDifference,
          icon: <img alt="check" src={CheckMarkIcon} />,
          bgColor: "#b9efd8",
          textColor: "#007b23",
        };
      }
      return {
        noOfDays: daysDifference,
        icon: <img alt="reject" className="rejected-icon" src={RejectedIcon} />,
        bgColor: "#ffe4e7",
        textColor: "#97000c",
        borderColor: "1px solid #FFC6CA",
        iconColor: "#97000c",
      };
    }

    if (timeoffType === "Frequency") {
      return { noOfDays };
    }
    return null;
  };

  const timeofRules = () => {
    return (
      <div className="request-timeoff-rules">
        <div className="request-timeoff-rules-heading heading-h5-semibold">Time off rules and regulations</div>

        {selectedLeaveTypeRules?.leavePolicies?.map(({ description, title, noOfDays, type }) => (
          <>
            <Divider className="rules-divider" light />

            <div className="request-timeoff-rules-subheading heading-h5-medium">{title}</div>

            {anniversaryBasedTimeOff && <RegulationChip leaveRequest={leaveRequest} type={type} noOfDays={noOfDays} />}
            {!anniversaryBasedTimeOff && noOfDays && (
              <div>
                <Chip
                  className="ml-1 notice-period-chip custom-chip mb-05"
                  style={{
                    background: getChipValuesForRegulations(type, noOfDays)?.bgColor,
                    color: getChipValuesForRegulations(type, noOfDays)?.textColor,
                    border: getChipValuesForRegulations(type, noOfDays)?.borderColor,
                  }}
                  label={
                    <div className="notice-period-chip-label flex para-body-s-regular" style={{ gap: "8px" }}>
                      {getChipValuesForRegulations(type, noOfDays)?.icon}
                      <div className="para-body-s-medium">
                        {getChipValuesForRegulations(type, noOfDays)?.noOfDays}{" "}
                        {getChipValuesForRegulations(type, noOfDays)?.noOfDays === 1 ? "Day" : "Days"}
                      </div>
                    </div>
                  }
                />
              </div>
            )}
            <div
              className="request-timeoff-rules-para time-off-rules-list para-body-s-regular"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </>
        ))}
      </div>
    );
  };

  const updateTotalLeaves = (startDate, endDate) => {
    const currentDate = new Date(startDate);

    while (currentDate <= new Date(endDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
  };

  const getLeavesValidationMessage = () => {
    if (selectedTimeOff?.leaves == 0 && selectedTimeOff?.holidays?.length > 0)
      return "You already have this day off as a holiday";
    if (selectedLeaveTypeRules?.taken >= selectedLeaveTypeRules?.noOfLeaves)
      return "You have already taken the maximum allowed leaves";
    if (selectedLeaveTypeRules?.taken < selectedLeaveTypeRules?.noOfLeaves)
      return "Selected number of days is more than the number of days allowed";
    return "You are unable to apply for leave on the selected dates.";
  };

  const handleSwappedLeavesChange = (data) => {
    setSwappedLeavesData(data);
    const { swapDays, timeOffDays, swapDaysInfo, isValid } = data || {};
    if (data && isValid) {
      setLeaveRequest({
        ...leaveRequest,
        selectedDateRange: timeOffDays,
        swapDays,
        swapDaysInfo,
      });
    }
  };

  const renderSwappedLeavesDateSelection = () => (
    <div className="date-range-wrapper">
      <SwappedLeavesDateSelection
        onChange={handleSwappedLeavesChange}
        leavesCalendar={leavesCalendar}
        validDates={validDates}
        holidaysCalendar={allHolidays?.data || []}
        swapDateFromAllowedInPast={selectedLeaveType?.swapDateFromAllowedInPast}
      />

      <Divider light />

      <div className="heading-h5-medium ml-1 mt-075 neutral-20">Enter note (Optional)</div>

      <textarea
        rows={3}
        value={leaveRequest?.description || ""}
        className="request-timeoff-textarea"
        onChange={(e) => {
          setLeaveRequest({
            ...leaveRequest,
            description: e.target.value,
          });
        }}
      />

      <div className="request-timeoff-action-btns ml-1">
        <Button variant="outlined" label="Cancel" className="" onClick={() => navigate("/main/timeoff")} />
        <Button
          variant="contained"
          label="Submit Request"
          isDisabled={!swappedLeavesData?.isValid}
          onClick={handleCreateLeaveRequest}
        />
      </div>
    </div>
  );

  const renderDateRangeWrapper = () => (
    <div className="date-range-wrapper">
      <div className="flex ml-1 mt-1 mb-075">
        <img src={DateRangeIcon} alt="date range picker icon" />
        <p className="neutral-20 ml-05 heading-h5-semibold">
          {canUserUploadDocuments && !anniversaryBasedTimeOff ? "Step 1/2 : " : ""} Select date range
        </p>
      </div>

      <Divider light />

      <Daterange
        key={`daterange-${leaveRequest?.selectedLeaveType}`}
        leavesCalender={leavesCalendar}
        allowWeekendSelection={!!selectedLeaveTypeRules?.allowWeekendSelection}
        onChange={(selectedDate, dateLabels, daysCount) => {
          setLeaveRequest({
            ...leaveRequest,
            selectedDateRange: selectedDate,
            daysCount,
          });
          setDayDuration("Full Day");
        }}
        validDates={validDates}
      />

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center"
        }}>
        <div className="request-timeoff-indicators-container ml-1">
          <div className="request-timeoff-indicator-box-label">
            <span className="request-timeoff-today-box-indicator" />
            <span className="title-sm overline">TODAY</span>
          </div>

          <div className="request-timeoff-indicator-box-label">
            <span className="request-timeoff-selected-box-indicator" />
            <span className="title-sm overline">SELECTED</span>
          </div>
        </div>

        <div className="flex gap-05 mr-1" style={{ marginBottom: "0px !important" }}>
          <span className="align-self-center calendar-dot-indicator-orange" />
          <p className="body-md align-self-center paragraph">Leaves</p>
          <span className="align-self-center calendar-dot-indicator-green" />
          <p className="body-md align-self-center paragraph">Holidays</p>
        </div>
      </Stack>

      <Divider light />

      {showDayDurationOptions && (
        <div className="w-50p">
          <div className="timeoff-selected ml-1">
            <div className="para-body-s-regular request-timeoff-label">Select day duration</div>
            <div className="flex gap-05 my-05">
              <RadioButton
                label="Full Day"
                checked={dayDuration === "Full Day"}
                onChange={() => setDayDuration("Full Day")}
                value="full"
                name="dayDuration"
                withContainer
              />
              <RadioButton
                label="Half Day"
                checked={dayDuration === "Half Day"}
                onChange={() => setDayDuration("Half Day")}
                value="half"
                name="dayDuration"
                withContainer
              />
            </div>
          </div>
        </div>
      )}

      <div className="timeoff-selected ml-1">
        <div className="para-body-s-regular request-timeoff-label">Time-off selected</div>
        <div className="para-body-m-medium primary-30 mt-05 pop-regular ">
          {selectedTimeOff?.leaves < 0 ? (
            "Selected date range not allowed"
          ) : (
            <>
              {`${selectedTimeOff?.leaves || 0} ${timeoffTypeLabel} leave${selectedTimeOff?.leaves > 1 ? "s " : " "}`} (
              {selectedLeaveTypeRules?.includeWeekends ? "Including weekends" : "Excluding weekends & holidays"})
            </>
          )}
        </div>

        <div className="pending-leave-duration mt-025">
          <div className="heading-h5-semibold">
            {moment(leaveRequest?.selectedDateRange?.startDate).format("MMM DD, YYYY")}
          </div>
          {convertDate(leaveRequest?.selectedDateRange?.startDate) !==
            convertDate(leaveRequest?.selectedDateRange?.endDate) && (
            <>
              <img className="pending-leave-arrow" src={LeftArrowSmall} alt="left arrow" />{" "}
              <div className="heading-h5-semibold">
                {moment(leaveRequest?.selectedDateRange?.endDate).format("MMM DD, YYYY")}
              </div>
            </>
          )}
        </div>

        <div className="pt-025">
          {!anniversaryBasedTimeOff && selectedTimeOff?.leaves < 1 && (
            <p className="label-md error-50">{getLeavesValidationMessage()}</p>
          )}
          {anniversaryBasedTimeOff && !selectedTimeOff?.success && (
            <p
              className="para-body-s-regular main-error"
              dangerouslySetInnerHTML={{ __html: selectedTimeOff?.message?.split(". ").join(". <br/>") }}
            />
          )}
        </div>
      </div>

      <div className="label-lg pop-regular mt-025 ml-1 mb-075">
        {Object.keys(selectedTimeOff?.holidays || {})?.length > 0
          ? `Holidays: ${selectedTimeOff?.holidays?.map((holiday) => holiday.Name).join(", ")}`
          : ""}
      </div>

      <Divider light />

      {anniversaryBasedTimeOff && canUserUploadDocuments && (
        <div className="mt-075 ml-1">
          <AttachDocuments
            showAddDocumentModal={showAddDocumentModal}
            setShowAddDocumentModal={setShowAddDocumentModal}
            leaveDocuments={filesToUpload}
            onAttached={(files) => {
              setFilesToUpload((prevFiles) => [...prevFiles, ...files]);
              setShowAddDocumentModal(false);
            }}
            onDelete={(file) =>
              setFilesToUpload((prevFiles) => prevFiles.filter((f) => f?.fileName !== file?.fileName))
            }
          />
        </div>
      )}

      <div className="heading-h5-medium ml-1 mt-075 neutral-20">
        Enter note {leaveTypes[0]?.countryCode !== "Peru" ? "(Optional)" : ""}
      </div>

      <textarea
        rows={3}
        value={leaveRequest?.description || ""}
        className="request-timeoff-textarea"
        onChange={(e) => {
          setLeaveRequest({
            ...leaveRequest,
            description: e.target.value,
          });
        }}
      />

      <div className="request-timeoff-action-btns ml-1">
        <Button variant="outlined" label="Cancel" className="" onClick={() => navigate("/main/timeoff")} />
        <Button
          variant="contained"
          label={!anniversaryBasedTimeOff && canUserUploadDocuments ? "Next" : "Submit Request"}
          isDisabled={selectedTimeOff?.leaves <= 0 || !selectedTimeOff?.success}
          onClick={() => {
            if (!anniversaryBasedTimeOff && canUserUploadDocuments) {
              setShowUploadDocumentSection(true);
            } else {
              handleCreateLeaveRequest();
            }
          }}
        />
      </div>
    </div>
  );

  const renderCancelMessage = () => (
    <div className="annual-accordian">
      Unfortunately, you have used up all your
      {timeoffTypeLabel} leaves. However, there are some leaves that are scheduled that you can cancel to request more
      {timeoffTypeLabel} leaves.
      <b onClick={() => navigate("/main/timeoff")} className="cursor-pointer">
        <u>Cancel scheduled leaves.</u>
      </b>
    </div>
  );

  const renderEmergencyMessage = () => (
    <div className="annual-accordian">
      Unfortunately, you have used up all your {timeoffTypeLabel} leaves. However, you can schedule unpaid time-off in
      case of an emergency.
    </div>
  );

  const getContent = () => {
    if (anniversaryBasedTimeOff) {
      return renderDateRangeWrapper();
    }

    const allowedTimeOff = getAllowedTimeOff();

    if (allowedTimeOff > 0 || allowedTimeOff === UNLIMITED) {
      return renderDateRangeWrapper();
    }
    if (calculateTotalDays() > 0) {
      return renderCancelMessage();
    }
    return renderEmergencyMessage();
  };

  useEffect(() => {
    if (submittingTimeoffRequest?.showSnackbar && submittingTimeoffRequest?.type)
      dispatch(
        showSnackbar({
          type: submittingTimeoffRequest?.type,
          message: submittingTimeoffRequest?.snackbarMessage,
          dispatch,
        })
      );
  }, [submittingTimeoffRequest]);
  useEffect(() => {
    calculateNoOfLeaves();
  }, [leaveRequest?.selectedDateRange, leaveRequest?.selectedLeaveType, dayDuration, isSwappedLeaveType]);

  useEffect(() => {
    if (anniversaryBasedTimeOff) {
      setCanUserUploadDocuments(leaveRequest?.additionalDocumentsRequired);
    } else {
      setCanUserUploadDocuments(leaveRequest?.attachDocument);
    }
  }, [leaveRequest]);

  useEffect(() => {
    setTimeoffRequestLoader(true);
    setSubmittingTimeoffRequest((prevState) => ({
      ...prevState,
      state: true,
      showSnackbar: false,
    }));

    if (anniversaryBasedTimeOff) {
      setTimeOffTypes(
        leaveTypes?.reduce((acc, leave) => {
          acc.push({
            label: leave?.leaveType,
            key: leave?.leaveTypeId,
            additionalDocumentsRequired: leave?.additionalDocumentsRequired,
            startDate: leave?.startDate,
            endDate: leave?.endDate,
            halfLeaveAllowed: leave?.halfLeaveAllowed,
            nextCycle: leave?.nextCycle || null,
            // Not yet returned by the backend (PRD-1879) — SwappedLeavesDateSelection falls
            // back to DEFAULT_SWAP_DATE_FROM_ALLOWED_IN_PAST until it lands.
            swapDateFromAllowedInPast: leave?.swapDateFromAllowedInPast,
          });
          return acc;
        }, [])
      );
    } else {
      setTimeOffTypes(
        leaveTypes?.reduce((acc, leave) => {
          if (leave?.visible) {
            acc.push({
              label: leave?.leaveType,
              key: leave?.leaveTypeId,
              attachDocument: leave?.attachDocument,
              startDate: leave?.startDate,
              endDate: leave?.endDate,
              halfLeaveAllowed: leave?.halfLeaveAllowed,
              // Not yet returned by the backend (PRD-1879) — SwappedLeavesDateSelection falls
              // back to DEFAULT_SWAP_DATE_FROM_ALLOWED_IN_PAST until it lands.
              swapDateFromAllowedInPast: leave?.swapDateFromAllowedInPast,
            });
          }
          return acc;
        }, [])
      );
    }

    const selectedType = leaveTypes.find(
      (type) => type.leaveTypeId === Number(new URLSearchParams(window.location.search).get("leaveTypeId"))
    );

    if (anniversaryBasedTimeOff) {
      setLeaveRequest({
        ...leaveRequest,
        selectedLeaveType:
          Number(new URLSearchParams(window.location.search).get("leaveTypeId")) ?? leaveTypes[0]?.leaveTypeId,
        additionalDocumentsRequired: selectedType?.additionalDocumentsRequired,
      });
    } else {
      setLeaveRequest({
        ...leaveRequest,
        selectedLeaveType:
          Number(new URLSearchParams(window.location.search).get("leaveTypeId")) ?? leaveTypes[0]?.leaveTypeId,
        isVisible: leaveTypes[0]?.visible,
        attachDocument: selectedType?.attachDocument,
      });
    }

    setSubmittingTimeoffRequest((prevState) => ({
      ...prevState,
      state: false,
      showSnackbar: false,
    }));
    setTimeoffRequestLoader(false);
  }, [leaveTypes]);

  useEffect(() => {
    const selectedTimeOffRules = leaveTypes.find((type) => type?.leaveTypeId === leaveRequest?.selectedLeaveType);
    setSelectedLeaveTypeRules(selectedTimeOffRules || null);
  }, [leaveTypes, leaveRequest?.selectedLeaveType]);

  useEffect(() => {
    dispatch(FetchUpcomingLeaves({ flag: anniversaryBasedTimeOff }));
    if (leaveTypes.length === 0)
      dispatch(FetchLeaveTypes({ flag: anniversaryBasedTimeOff, futureCycleFlag: futureCycleLeavesFiling }));
  }, [anniversaryBasedTimeOff]);

  useEffect(() => {
    if (leaveRequest?.selectedDateRange) {
      updateTotalLeaves(leaveRequest?.selectedDateRange?.startDate, leaveRequest?.selectedDateRange?.endDate);
    }
  }, [leaveRequest?.selectedDateRange]);

  useEffect(() => {
    if (selectedLeaveTypeIdFromCard) {
      setLeaveRequest((prev) => ({
        ...prev,
        selectedLeaveType: selectedLeaveTypeIdFromCard,
      }));
    }
  }, [selectedLeaveTypeIdFromCard]);

  useEffect(() => {
    const holidaysRequest = `${baseUrl}/employee/publicHolidays`;
    const region = userAdditionalInfo?.country;

    if (region)
      dispatch(
        GetAllHolidays({
          request: holidaysRequest,
          region,
          startDate: addDays(new Date(), -365),
          endDate: addDays(new Date(), 365),
        })
      );
  }, [userAdditionalInfo]);

  useEffect(() => {
    const dates = [];

    upcomingLeaves?.map((leave) => dates.push(...getDatesBetweenDateRange(leave?.dateFrom, leave?.dateTo)));

    setLeavesCalendar(dates || []);
  }, [upcomingLeaves]);

  useEffect(() => {
    if (leaveTypes.length > 0) {
      setValidDates({
        startDate: leaveTypes[0]?.startDate,
        endDate: (futureCycleLeavesFiling && leaveTypes[0]?.nextCycle?.maxFilingDate) || leaveTypes[0]?.endDate,
      });
    }
  }, [leaveTypes]);

  return (
    <>
      {submittingTimeoffRequest?.state && <PageLoader />}
      {!timeoffRequestLoader && (
        <div className="manage-timeoff-wrapper">
          <TimeoffLayout />

          <div className="request-timeoff-container">
            <div className="request-timeoff-type-wrapper">
              <Stack direction="row" spacing={2}>
                <div>
                  <div className="heading-h5-medium request-timeoff-type-label">Select type</div>

                  <CustomDropdown
                    options={timeOffTypes}
                    disabled={!!showUploadDocumentSection}
                    onSelect={(key) => {
                      const selectedType = timeOffTypes.find((type) => type.key === key);
                      setSelectedTimeOff({});
                      if (anniversaryBasedTimeOff) {
                        setLeaveRequest({
                          ...leaveRequest,
                          selectedLeaveType: key,
                          additionalDocumentsRequired: selectedType?.additionalDocumentsRequired,
                        });
                        setDayDuration("Full Day");
                      } else {
                        setLeaveRequest({
                          ...leaveRequest,
                          selectedLeaveType: key,
                          attachDocument: selectedType?.attachDocument,
                        });
                      }
                      setValidDates({
                        startDate: selectedType?.startDate ? selectedType.startDate : null,
                        endDate:
                          (futureCycleLeavesFiling && selectedType?.nextCycle?.maxFilingDate) ||
                          (selectedType?.endDate ? selectedType.endDate : null),
                      });
                    }}
                    buttonLabel={timeoffTypeLabel}
                  />
                </div>
                {!isSwappedLeaveType && (
                  <div>
                    <div className="heading-h5-medium request-timeoff-type-label">Allowed paid time-off</div>

                    <div className="para-body-m-medium request-timeoff-type-value">{getAllowedTimeOff()}</div>
                  </div>
                )}
              </Stack>
              {!isSwappedLeaveType && getAllowedTimeOff() > 0 && !!calculateTotalDays() && (
                <Chip
                  className="request-timeoff-type-chip"
                  label={`You have ${calculateTotalDays()} days scheduled and pending in the category. You can cancel them and make a new request here`}
                />
              )}
            </div>
            {userAdditionalInfo?.timeoffCountry === "Peru" &&
              leaveRequest?.selectedLeaveType === leaveTypeIds.Annual && (
                <CustomAlert
                  severity="info"
                  variant="standard"
                  className="mb-1 peru-timeoff-note"
                  description="Please ensure your vacation request includes all continuous calendar days until the day before you return to work, including holidays and weekly rest days where applicable."
                />
              )}

            <div className="request-timeoff-wrapper">
              {isSwappedLeaveType && swappedLeavesRevamp ? (
                <>
                  {renderSwappedLeavesDateSelection()}
                  {timeofRules()}
                </>
              ) : !anniversaryBasedTimeOff && showUploadDocumentSection ? (
                <>
                  {" "}
                  <div className="date-range-wrapper w-100p">
                    <div className="flex ml-1 mt-1 mb-075 " style={{ width: "654px" }}>
                      <img src={DateRangeIcon} alt="date range picker icon" />
                      <p className="neutral-20 ml-05"> Step 2/2 : Attach your medical documents</p>
                    </div>

                    <Divider light />

                    <div className="timeoff-selected ml-1">
                      <div className="label-lg neutral-60 pop-regular">Time-off selected</div>
                      <div className="step-2-dates">
                        <div className="title-md primary-30 mt-05 pop-regular ">
                          {selectedTimeOff?.leaves < 0 ? (
                            "Selected daterange not allowed"
                          ) : (
                            <>
                              {`${selectedTimeOff?.leaves} ${timeoffTypeLabel} leave${selectedTimeOff?.leaves > 1 ? "s " : " "}`}
                              (
                              {selectedLeaveTypeRules?.includeWeekends
                                ? "Including weekends"
                                : "Excluding weekends & holidays"}
                              )
                            </>
                          )}
                        </div>
                        <Button
                          variant="outlined"
                          className="mr-1 edit-button"
                          onClick={() => {
                            setShowUploadDocumentSection(false);
                          }}
                          label="Edit"
                        />
                      </div>

                      <div className="pending-leave-duration mt-025">
                        <div className="pending-leave-date">
                          {moment(leaveRequest?.selectedDateRange?.startDate).format("MMM DD, YYYY")}
                        </div>
                        <img className="pending-leave-arrow" src={LeftArrowSmall} alt="left arrow" />{" "}
                        <div className="pending-leave-date">
                          {moment(leaveRequest?.selectedDateRange?.endDate).format("MMM DD, YYYY")}
                        </div>
                      </div>
                    </div>

                    <div className="label-lg pop-regular neutral-60 mt-025 ml-1 mb-075">
                      {Object.keys(selectedTimeOff?.holidays || {})?.length > 0
                        ? `Holidays: ${selectedTimeOff?.holidays?.map((holiday) => holiday.Name).join(", ")}`
                        : ""}
                    </div>

                    <div className="label-lg ml-1 mb-1">
                      Note:
                      <span className="text-italic"> {leaveRequest?.description}</span>
                    </div>
                    <Divider light />

                    <div>
                      <p className="neutral-20 ml-1 mt-15">Attach doctor's note</p>
                      <p className="ml-1 label-sm neutral-60">You can attach as many documents as needed</p>

                      <FileUploader onFileUpload={(files) => setFilesToUpload(files)} />
                    </div>

                    <div className="label-lg ml-1 mt-05">
                      Enter note {leaveTypes[0]?.countryCode !== "Peru" ? "(Optional)" : ""}{" "}
                    </div>

                    <textarea
                      rows={3}
                      value={leaveRequest?.description || ""}
                      className="request-timeoff-textarea"
                      onChange={(e) => {
                        setLeaveRequest({
                          ...leaveRequest,
                          description: e.target.value,
                        });
                      }}
                    />
                    <div className="request-timeoff-action-btns ml-1 mt-1">
                      <Button
                        variant="outlined"
                        label="Cancel"
                        className=""
                        onClick={() => navigate("/main/timeoff")}
                      />
                      <Button variant="contained" onClick={handleCreateLeaveRequest} label="Confirm" />
                    </div>
                  </div>
                  {timeofRules()}
                </>
              ) : (
                <>
                  {getContent()}
                  {timeofRules()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RequestTimeoff;
